// A scope is an actor (e.g. a community) whose current policy claim decides which claims take effect.
// The scope only appends. It never edits or deletes. Every decision carries its evidence (trace).
//
// v2, L3′: a witnessed scope signs a receipt claim for every submission that passes signature checks. Receipts
// chain (each refers to the one before), so order and admission time are attributable to the witness key and a
// fork is provable (see equivocation). Unwitnessed scopes keep v1 behaviour and say so (`witnessed: false`).

import { canonical, deepFreeze, embeddedClaims, idOfSync, isJsonClaim, sign, unsigned, verify } from './claim.ts';
import type { Actor, ActorId, Claim, Draft, Json, UnsignedClaim } from './claim.ts';
import { PolicyError, assertPolicy, isTimed, run, validatePolicy } from './rules.ts';
import type { Ctx, Entry, LogIndex, Policy, TraceNode } from './rules.ts';

export type Reason =
  | 'admitted'
  | 'bad-signature'
  | 'bad-embedded-signature'
  | 'wrong-scope' // v2: the claim is bound to another scope
  | 'duplicate'
  | 'unknown-predicate'
  | 'bad-policy' // v2: a policy claim whose body fails validatePolicy
  | 'rule'
  | 'rule-error' // v2: the evaluator met an internal error; denied, never thrown
  | 'unreceipted-evidence'; // v2.2: embeds a claim bound to another scope without that scope's admitting receipt

export interface Decision {
  admitted: boolean;
  reason: Reason;
  seq?: number;
  trace: TraceNode[];
  evidence: string[]; // claim ids the decision rests on
  receipt?: Claim; // v2: the witness receipt for this submission
  error?: string; // v2: present when reason is 'rule-error'
}

export interface Rejected {
  claim: Claim;
  at: number;
  decision: Decision;
}

export interface Receipted {
  claim: Claim;
  receipt: Claim;
}

export interface ReceiptBody {
  n: number;
  at: number;
  admitted: boolean;
  reason: Reason;
}

export interface ScopeOptions {
  // How many rejections stay in memory. Their receipts are the durable record; a long-lived scope persists
  // each Decision.receipt as it is issued. Default 1000.
  retainRejected?: number;
}

export type ScopeFault = 'bad-genesis' | 'wrong-witness' | 'no-witness-key' | 'clock-regression' | 'clock-skew';

export class ScopeError extends Error {
  readonly code: ScopeFault;
  constructor(code: ScopeFault, message: string) {
    super(message);
    this.name = 'ScopeError';
    this.code = code;
  }
}

export type ReplayFault = 'bad-genesis' | 'bad-signature' | 'broken-chain' | 'decision-mismatch' | 'clock-skew';

export class ReplayError extends Error {
  readonly fault: ReplayFault;
  readonly n: number; // receipt index of the first divergence
  readonly claim: string | null; // the claim id at that slot
  constructor(fault: ReplayFault, n: number, claim: string | null, detail: string) {
    super(`history diverges at receipt ${n} (${fault}): ${detail}`);
    this.name = 'ReplayError';
    this.fault = fault;
    this.n = n;
    this.claim = claim;
  }
}

// The one predicate the law itself knows: a policy is a claim, admitted under the policy before it.
const POLICY = 'policy';
// The witness's own predicate. Receipts are claims but never log entries.
const RECEIPT = 'receipt';
// How many entries restore() verifies concurrently.
const RESTORE_BATCH = 256;
// v2.2, L3′ time bound (REDTEAM-3 W1, G5, S2): a receipt's time and its claim's issuedAt agree within this many ms,
// unless the policy says otherwise in params.receiptSkew. The witness clock is the scope's only clock; the author's
// clock is the only independent one it meets. Without the bound one skewed reading (400 days ahead) is receipted, and
// monotonicity makes it permanent: the operator is wedged (clock-regression) or $now is frozen in the future.
export const DEFAULT_RECEIPT_SKEW = 60 * 60_000;
export function receiptSkew(p: Policy): number {
  const k = p.params.receiptSkew;
  return typeof k === 'number' && Number.isFinite(k) && k > 0 ? k : DEFAULT_RECEIPT_SKEW;
}
function skewFault(at: number, claim: Claim, p: Policy): string | null {
  const k = receiptSkew(p);
  return Math.abs(at - claim.issuedAt) > k ? `receipt time ${at} and issuedAt ${claim.issuedAt} differ by more than ${k} ms` : null;
}

// v2.2 (REDTEAM-3 K5): what the scope hands out is a view, never its own arrays. Reads pass through; writes throw.
const READONLY: ProxyHandler<object> = {
  set: () => false,
  defineProperty: () => false,
  deleteProperty: () => false,
  setPrototypeOf: () => false,
  preventExtensions: () => false,
};
function readonlyArray<T>(a: T[]): readonly T[] {
  return new Proxy(a, READONLY) as readonly T[];
}
class ReadonlyIndex implements ReadonlyMap<string, readonly Entry[]> {
  readonly #m: Map<string, Entry[]>;
  readonly #views = new WeakMap<Entry[], readonly Entry[]>();
  constructor(m: Map<string, Entry[]>) {
    this.#m = m;
  }
  #view(l: Entry[]): readonly Entry[] {
    let v = this.#views.get(l);
    if (!v) this.#views.set(l, (v = readonlyArray(l)));
    return v;
  }
  get(k: string): readonly Entry[] | undefined {
    const l = this.#m.get(k);
    return l ? this.#view(l) : undefined;
  }
  has(k: string): boolean {
    return this.#m.has(k);
  }
  get size(): number {
    return this.#m.size;
  }
  *entries(): MapIterator<[string, readonly Entry[]]> {
    for (const [k, l] of this.#m) yield [k, this.#view(l)];
  }
  keys(): MapIterator<string> {
    return this.#m.keys();
  }
  *values(): MapIterator<readonly Entry[]> {
    for (const l of this.#m.values()) yield this.#view(l);
  }
  forEach(f: (v: readonly Entry[], k: string, m: ReadonlyMap<string, readonly Entry[]>) => void): void {
    for (const [k, l] of this.#m) f(this.#view(l), k, this);
  }
  [Symbol.iterator](): MapIterator<[string, readonly Entry[]]> {
    return this.entries();
  }
}

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const deny = (reason: Reason): Decision => ({ admitted: false, reason, trace: [], evidence: [] });

// What a given actor may see of a claim: scope-wide claims, their own, and claims whose audience names them.
export function visibleTo(c: Claim, viewer: ActorId | null): boolean {
  if (c.audience === 'scope') return true;
  if (viewer === null || viewer === '') return false;
  return c.author === viewer || c.audience.includes(viewer);
}

export function receiptBody(r: Claim): ReceiptBody | null {
  const b = r.body;
  if (!isObj(b) || typeof b.n !== 'number' || typeof b.at !== 'number' || typeof b.admitted !== 'boolean' || typeof b.reason !== 'string') return null;
  return b as unknown as ReceiptBody;
}

// Why a receipt does not fit the slot (claim, prev, n) under this witness; null when it does. Does not check the signature.
export function receiptFault(r: Claim, claim: Claim, prev: Claim | null, n: number, witness: ActorId): string | null {
  if (r.predicate !== RECEIPT) return 'not a receipt';
  if (r.author !== witness) return 'signed by another key';
  if (r.subject !== null) return 'receipt subject must be null';
  if (r.refs.length !== (prev ? 2 : 1) || r.refs[0] !== claim.id || (prev && r.refs[1] !== prev.id)) return 'refs do not chain';
  const b = receiptBody(r);
  if (!b) return 'malformed receipt body';
  if (b.n !== n) return `receipt n is ${b.n}, slot is ${n}`;
  if (r.issuedAt !== b.at) return 'issuedAt differs from body.at';
  if (canonical(r.audience) !== canonical(claim.audience)) return 'audience differs from the claim';
  if (prev) {
    const pb = receiptBody(prev);
    if (pb && b.at < pb.at) return `receipt time ${b.at} precedes the previous receipt (${pb.at})`; // v2.1, L3′: time is monotone on the chain
  }
  return null;
}

// Proof of a fork (v2.2, REDTEAM-3 W2/W3): two different receipts by the same witness, for the same scope, at the same
// position n. A position is (scope, n), not (witness, predecessor): one deployment witness serves many scopes, so two
// genesis receipts (n = 0, no predecessor) of different scopes are not comparable, while two receipts at one n of one
// scope are a fork whatever their predecessors (a fork at a private slot leaves the next public slots differing in
// their refs[1], and those are what members can compare).
export async function equivocation(r1: Claim, r2: Claim): Promise<boolean> {
  if (r1.predicate !== RECEIPT || r2.predicate !== RECEIPT) return false;
  if (r1.author !== r2.author) return false;
  if (typeof r1.scope !== 'string' || r1.scope !== r2.scope) return false;
  const [b1, b2] = [receiptBody(r1), receiptBody(r2)];
  if (!b1 || !b2 || b1.n !== b2.n) return false;
  if (r1.id === r2.id) return false;
  return (await verify(r1)) && (await verify(r2));
}

// v2.2 (REDTEAM-3 X2): a claim bound to another scope proves authorship, not admission. Embedded as evidence it counts
// only next to that scope's receipt admitting it (both under $claim, so L2 verifies both). The kernel cannot know the
// other scope's witness; a policy that weighs such evidence pins it (reads the receipt's author). Receipts themselves
// are the witness's word and need no receipt.
export function unreceiptedEvidence(body: Json, here: ActorId): Claim | null {
  const inner = embeddedClaims(body);
  for (const c of inner) {
    if (!c || typeof c !== 'object' || c.predicate === RECEIPT) continue;
    if (typeof c.scope !== 'string' || c.scope === here) continue;
    const ok = inner.some((r) => r && r.predicate === RECEIPT && r.scope === c.scope && Array.isArray(r.refs) && r.refs[0] === c.id && receiptBody(r)?.admitted === true);
    if (!ok) return c;
  }
  return null;
}

export class Scope {
  readonly id: ActorId;
  readonly witness: ActorId | null;
  #signer: Actor | null;
  #log: Entry[] = [];
  #byId = new Map<string, Entry>();
  #ix = { byPredicate: new Map<string, Entry[]>(), byAuthor: new Map<string, Entry[]>(), bySubject: new Map<string, Entry[]>(), refsTo: new Map<string, Entry[]>() };
  #rejected: Rejected[] = [];
  readonly #logView = readonlyArray(this.#log);
  readonly #rejectedView = readonlyArray(this.#rejected);
  readonly #ixView: LogIndex = Object.freeze({
    byPredicate: new ReadonlyIndex(this.#ix.byPredicate),
    byAuthor: new ReadonlyIndex(this.#ix.byAuthor),
    bySubject: new ReadonlyIndex(this.#ix.bySubject),
    refsTo: new ReadonlyIndex(this.#ix.refsTo),
  });
  #evicted = 0;
  #retain: number;
  #policy: Policy;
  #receipts = 0; // receipts issued so far; the next receipt's n
  #lastReceipt: Claim | null = null;
  #queue: Promise<void> = Promise.resolve();

  private constructor(genesis: Claim, policy: Policy, witness: ActorId | null, signer: Actor | null, opts: ScopeOptions) {
    this.id = genesis.author;
    this.witness = witness;
    this.#signer = signer;
    this.#policy = policy;
    this.#retain = opts.retainRejected ?? 1000;
  }

  // Genesis: the scope actor's first policy claim is admitted by definition. With a witness, it gets receipt 0.
  // The witness must be the key the policy names in params.witness, else the genesis author.
  static async genesis(policyClaim: Claim, at: number, witness?: Actor, opts: ScopeOptions = {}): Promise<Scope> {
    const s = await Scope.#boot(policyClaim, witness?.id ?? null, witness ?? null, opts);
    if (witness) {
      const skew = skewFault(at, policyClaim, s.#policy);
      if (skew) throw new ScopeError('clock-skew', skew);
    }
    const receipt = await s.#issue(policyClaim, { admitted: true, reason: 'admitted' }, at);
    s.#append(policyClaim, at, receipt);
    return s;
  }

  static async #boot(policyClaim: Claim, witnessId: ActorId | null, signer: Actor | null, opts: ScopeOptions): Promise<Scope> {
    if (policyClaim.predicate !== POLICY) throw new ScopeError('bad-genesis', 'genesis must be a policy claim');
    if (!(await verify(policyClaim))) throw new ScopeError('bad-genesis', 'genesis signature invalid');
    if (policyClaim.scope !== null && policyClaim.scope !== undefined && policyClaim.scope !== policyClaim.author) {
      throw new ScopeError('bad-genesis', 'genesis is bound to another scope');
    }
    const policy = assertPolicy(policyClaim.body);
    if (witnessId !== null) {
      const named = policy.params.witness;
      const expected = typeof named === 'string' ? named : policyClaim.author;
      if (witnessId !== expected) throw new ScopeError('wrong-witness', `the witness must be ${expected}`);
    }
    return new Scope(policyClaim, policy, witnessId, signer, opts);
  }

  get witnessed(): boolean {
    return this.witness !== null;
  }

  get policy(): Readonly<Policy> {
    return this.#policy;
  }

  // Read-only views (v2.2): app code cannot append, truncate or forge entries through them.
  get log(): readonly Entry[] {
    return this.#logView;
  }

  get index(): LogIndex {
    return this.#ixView;
  }

  // The recent window of rejections (see ScopeOptions.retainRejected).
  get rejected(): readonly Rejected[] {
    return this.#rejectedView;
  }

  get evictedRejected(): number {
    return this.#evicted;
  }

  get lastReceipt(): Claim | null {
    return this.#lastReceipt;
  }

  entry(id: string): Entry | undefined {
    return this.#byId.get(id);
  }

  // Receipted submissions in receipt order: what replay() and restore() take. Whole only while no rejection has
  // been evicted (evictedRejected === 0); a long-lived scope persists each Decision.receipt instead.
  history(): Receipted[] {
    if (this.witness === null) return [];
    const out: Receipted[] = [];
    for (const e of this.#log) if (e.receipt) out.push({ claim: e.claim, receipt: e.receipt });
    for (const r of this.#rejected) if (r.decision.receipt) out.push({ claim: r.claim, receipt: r.decision.receipt });
    return out.sort((a, b) => receiptBody(a.receipt)!.n - receiptBody(b.receipt)!.n);
  }

  // Submissions are serialised: two in-flight submits must not read the same predecessor receipt or the same
  // log length. A scope has one writer; concurrent callers queue behind each other.
  async submit(claim: Claim, at: number): Promise<Decision> {
    if (this.witness !== null && this.#signer === null) {
      throw new ScopeError('no-witness-key', 'this replica verifies receipts but holds no witness key; it cannot extend a witnessed log');
    }
    const turn = this.#queue.then(() => this.#submit(claim, at, null));
    this.#queue = turn.then(
      () => undefined,
      () => undefined,
    );
    return turn;
  }

  // Shared by submit (signs a fresh receipt) and replay (uses the receipt under verification).
  async #submit(claim: Claim, at: number, given: Claim | null): Promise<Decision> {
    // L3′ (v2.1): a witnessed scope never issues a receipt dated before the one it last issued. The operator's clock,
    // not the claim, is at fault; refusing is an exception, not a verdict, so nothing is receipted.
    if (given === null && this.#signer !== null && this.#lastReceipt !== null) {
      const prev = receiptBody(this.#lastReceipt)!.at;
      if (at < prev) throw new ScopeError('clock-regression', `submission time ${at} precedes the last receipt (${prev})`);
    }
    const author = typeof claim.author === 'string' ? claim.author : '';
    if (!(await verify(claim))) return this.#reject(claim, at, deny('bad-signature'), author);
    // v2.2 (REDTEAM-3 L5): bytes JSON cannot carry are not the claim that was signed ({x: Infinity} hashes as {x: null}).
    if (!isJsonClaim(claim)) return this.#reject(claim, at, deny('bad-signature'), author);
    // v2.2 (REDTEAM-3 S3; REDTEAM-2 cost:2): a predicate the policy has no rule for is refused before any embedded
    // verification and is not witnessed: nothing in it can take effect, so it costs no receipt, no storage, no replay.
    // (Replaying a pre-v2.2 history that receipted one still works: the given receipt is consumed.)
    if (!Object.prototype.hasOwnProperty.call(this.#policy.rules, claim.predicate) && given === null) {
      return this.#reject(claim, at, deny('unknown-predicate'), author);
    }
    for (const inner of embeddedClaims(claim.body)) if (!(await verify(inner))) return this.#reject(claim, at, deny('bad-embedded-signature'), author);
    let d: Decision;
    if (claim.scope !== null && claim.scope !== undefined && claim.scope !== this.id) d = deny('wrong-scope');
    else if (this.#byId.has(claim.id)) d = deny('duplicate');
    else if (claim.predicate === POLICY && validatePolicy(claim.body).length > 0) d = deny('bad-policy');
    else d = this.evaluate(claim, at);
    if (given === null && this.#signer !== null) {
      const skew = skewFault(at, claim, this.#policy);
      if (skew) throw new ScopeError('clock-skew', skew); // the clock or the claim is off: refuse to witness, receipt nothing
    }
    const receipt = given ? this.#advance(given) : await this.#issue(claim, d, at);
    if (receipt) d = { ...d, receipt };
    if (!d.admitted) return this.#reject(claim, at, d, author);
    const seq = this.#append(claim, at, receipt);
    if (claim.predicate === POLICY) this.#policy = claim.body as unknown as Policy; // validated above
    return this.#redact({ ...d, seq }, author);
  }

  // Capability is not stored anywhere: it is the answer to "would this claim be admitted now?"
  // The answer is cut to what `requester` may see (default: the author asking about their own draft): ids, and (v2.1)
  // every value on a node that hid ids or rests on one, so no private number leaks through a sum or a comparison.
  // Residuals, documented (REDTEAM-2 rows 8, 9): the verdict under a requester-chosen draft is one comparison per
  // query (a binary search over a private number is ~log2(range) queries), and `hidden` still counts what was cut.
  // A coarse verdict-only answer for third parties would close the second, but an overseer reading another actor's
  // gate (the UX case view) and a replica's `would` are legitimate third-party readers; closing both needs
  // policy-declared third-party-queryable predicates, not a kernel default.
  //
  // v2.2 (REDTEAM-3 K1): for a third party (requester ≠ author) a node whose value depended on `now` keeps no `hidden`
  // count. With a chosen (or polled) `now`, a rolling-window count of another member's hidden claims gave their
  // admission times to the millisecond. Hosts must not let third parties choose `now` (the substrate pins it).
  would(author: ActorId, draft: Draft, now: number, requester: ActorId = author): Decision {
    return this.#redact(this.evaluate(unsigned(author, draft), now), requester, requester !== author);
  }

  // Unredacted evaluation: the operator's view. Never throws.
  evaluate(cand: UnsignedClaim | Claim, now: number): Decision {
    const rules = this.#policy.rules;
    if (!Object.prototype.hasOwnProperty.call(rules, cand.predicate)) return deny('unknown-predicate');
    // v2.1 (g): params.boundPredicates names the claims that must carry scope = this scope; an unbound one is refused
    // before any rule runs, so a claim of that kind cannot be forwarded from a sibling scope.
    const bound = this.#policy.params.boundPredicates;
    if (Array.isArray(bound) && bound.includes(cand.predicate) && cand.scope !== this.id) return deny('wrong-scope');
    // v2.2 (X2): evidence bound to another scope needs that scope's admitting receipt next to it.
    try {
      if (unreceiptedEvidence(cand.body, this.id) !== null) return deny('unreceipted-evidence');
    } catch (e) {
      return { admitted: false, reason: 'rule-error', trace: [], evidence: [], error: e instanceof Error ? `${e.name}: ${e.message}` : String(e) };
    }
    const ctx: Ctx = { cand, log: this.#log, byId: this.#byId, now, params: { ...this.#policy.params, scope: this.id }, index: this.#ix };
    const v = run(rules[cand.predicate]!, ctx);
    const evidence = evidenceOf(v.trace);
    if (v.error !== undefined) return { admitted: false, reason: 'rule-error', trace: v.trace, evidence, error: v.error };
    return { admitted: v.ok, reason: v.ok ? 'admitted' : 'rule', trace: v.trace, evidence };
  }

  // What a given actor may see: scope-wide claims, plus claims whose audience names them.
  visible(viewer: ActorId): Claim[] {
    return this.#log.map((e) => e.claim).filter((c) => visibleTo(c, viewer));
  }

  // Audit path. Verifies every receipt signature and the chain, re-runs every rule at the receipted time, and throws
  // a ReplayError naming the first divergence. `history` is what history() returns: genesis first, receipt order.
  // Without `witness` the replica can answer would() but not submit() (it holds no key to extend the chain).
  static async replay(genesisClaim: Claim, history: readonly Receipted[], witnessId: ActorId, witness?: Actor, opts: ScopeOptions = {}): Promise<Scope> {
    const s = await Scope.#open(genesisClaim, history, witnessId, witness, opts);
    let prev = history[0]!.receipt;
    for (let i = 1; i < history.length; i++) {
      const { claim, receipt } = history[i]!;
      await Scope.#checkReceipt(receipt, claim, prev, i, witnessId);
      const b = receiptBody(receipt)!;
      const skew = skewFault(b.at, claim, s.#policy);
      if (skew) throw new ReplayError('clock-skew', i, claim.id, skew);
      if (b.reason === 'bad-signature' || b.reason === 'bad-embedded-signature') {
        throw new ReplayError('decision-mismatch', i, claim.id, 'a receipt exists for a submission that fails signature checks');
      }
      const d = await s.#submit(claim, b.at, receipt);
      if (d.admitted !== b.admitted || d.reason !== b.reason) {
        throw new ReplayError('decision-mismatch', i, claim.id, `receipt says ${b.admitted ? 'admitted' : b.reason}; re-evaluation says ${d.admitted ? 'admitted' : d.reason}`);
      }
      prev = receipt;
    }
    return s;
  }

  // Restore (v2.1): the hash chain plus EVERY signature: each receipt under the witness key (L3′) and each claim and
  // embedded claim under its author key (L2). Rules are not re-run, so admission under policy (L3) and the policy
  // lineage (L5) are still the witness's word; replay() is the audit path that checks those too.
  static async restore(genesisClaim: Claim, history: readonly Receipted[], witnessId: ActorId, witness?: Actor, opts: ScopeOptions = {}): Promise<Scope> {
    const s = await Scope.restoreTrusted(genesisClaim, history, witnessId, witness, opts);
    // Signature checks are independent of each other: run them in batches so WebCrypto can use its thread pool.
    const check = async (i: number): Promise<ReplayError | null> => {
      const { claim, receipt } = history[i]!;
      if (!(await verify(receipt))) return new ReplayError('bad-signature', i, claim.id, 'receipt signature invalid');
      const b = receiptBody(receipt)!;
      if (b.reason === 'bad-signature' || b.reason === 'bad-embedded-signature') return null; // receipted as unsigned; nothing to check
      if (!(await verify(claim))) return new ReplayError('bad-signature', i, claim.id, 'claim signature invalid: the witness receipted an unsigned claim');
      for (const inner of embeddedClaims(claim.body)) {
        if (!(await verify(inner))) return new ReplayError('bad-signature', i, claim.id, 'embedded claim signature invalid');
      }
      return null;
    };
    for (let from = 1; from < history.length; from += RESTORE_BATCH) {
      const to = Math.min(history.length, from + RESTORE_BATCH);
      const faults = await Promise.all(Array.from({ length: to - from }, (_, k) => check(from + k)));
      const first = faults.find((f) => f !== null);
      if (first) throw first;
    }
    return s;
  }

  // Trusted fast path (the v2 `restore`). Trusts the witness for L2, L3 and L5: one signature (the last receipt) plus the
  // hash chain authenticates the bytes of every receipt and every claim; claim signatures are not re-checked and rules
  // are not re-run. Use it only where the witness key is the caller's own (a sequencer waking up on its own storage).
  static async restoreTrusted(genesisClaim: Claim, history: readonly Receipted[], witnessId: ActorId, witness?: Actor, opts: ScopeOptions = {}): Promise<Scope> {
    const n = history.length;
    if (n === 0) throw new ReplayError('broken-chain', 0, genesisClaim.id, 'empty history');
    const last = history[n - 1]!;
    if (last.receipt.author !== witnessId || !(await verify(last.receipt))) {
      throw new ReplayError('bad-signature', n - 1, last.claim.id, 'the last receipt does not verify under the witness');
    }
    let expected = last.receipt.id;
    for (let i = n - 1; i >= 0; i--) {
      const { claim, receipt } = history[i]!;
      if (receipt.id !== expected || idOfSync(receipt) !== receipt.id) throw new ReplayError('broken-chain', i, claim.id, 'receipt bytes do not match the chain');
      if (receipt.refs[0] !== claim.id || idOfSync(claim) !== claim.id) throw new ReplayError('broken-chain', i, claim.id, 'claim bytes do not match the receipt');
      const fault = receiptFault(receipt, claim, i > 0 ? history[i - 1]!.receipt : null, i, witnessId);
      if (fault !== null) throw new ReplayError('broken-chain', i, claim.id, fault);
      expected = receipt.refs[1] ?? '';
    }
    const s = await Scope.#open(genesisClaim, history, witnessId, witness, opts);
    for (let i = 1; i < n; i++) {
      const { claim, receipt } = history[i]!;
      const b = receiptBody(receipt)!;
      const skew = skewFault(b.at, claim, s.#policy);
      if (skew) throw new ReplayError('clock-skew', i, claim.id, skew);
      s.#advance(receipt);
      if (b.admitted) {
        s.#append(claim, b.at, receipt);
        if (claim.predicate === POLICY) s.#policy = assertPolicy(claim.body);
      } else {
        s.#remember({ claim, at: b.at, decision: { admitted: false, reason: b.reason, trace: [], evidence: [], receipt } });
      }
    }
    return s;
  }

  // Shared start of replay and restore: boot from the genesis and verify receipt 0.
  static async #open(genesisClaim: Claim, history: readonly Receipted[], witnessId: ActorId, witness: Actor | undefined, opts: ScopeOptions): Promise<Scope> {
    if (witness && witness.id !== witnessId) throw new ScopeError('wrong-witness', `the witness must be ${witnessId}`);
    const first = history[0];
    if (!first || first.claim.id !== genesisClaim.id) throw new ReplayError('broken-chain', 0, genesisClaim.id, 'history must begin with the genesis and its receipt');
    let s: Scope;
    try {
      s = await Scope.#boot(genesisClaim, witnessId, witness ?? null, opts);
    } catch (e) {
      if (e instanceof ScopeError || e instanceof PolicyError) throw new ReplayError('bad-genesis', 0, genesisClaim.id, e.message);
      throw e;
    }
    await Scope.#checkReceipt(first.receipt, genesisClaim, null, 0, witnessId);
    const b = receiptBody(first.receipt)!;
    if (!b.admitted || b.reason !== 'admitted') throw new ReplayError('decision-mismatch', 0, genesisClaim.id, 'the genesis receipt must be an admission');
    const skew = skewFault(b.at, genesisClaim, s.#policy);
    if (skew) throw new ReplayError('clock-skew', 0, genesisClaim.id, skew);
    s.#advance(first.receipt);
    s.#append(genesisClaim, b.at, first.receipt);
    return s;
  }

  static async #checkReceipt(receipt: Claim, claim: Claim, prev: Claim | null, n: number, witnessId: ActorId): Promise<void> {
    if (!(await verify(receipt))) throw new ReplayError('bad-signature', n, claim.id, 'receipt signature invalid');
    const fault = receiptFault(receipt, claim, prev, n, witnessId);
    if (fault !== null) throw new ReplayError(fault === 'signed by another key' ? 'bad-signature' : 'broken-chain', n, claim.id, fault);
  }

  async #issue(claim: Claim, d: { admitted: boolean; reason: Reason }, at: number): Promise<Claim | undefined> {
    if (this.#signer === null) return undefined;
    const prev = this.#lastReceipt;
    const r = await sign(this.#signer, {
      predicate: RECEIPT,
      subject: null,
      refs: prev ? [claim.id, prev.id] : [claim.id],
      body: { n: this.#receipts, at, admitted: d.admitted, reason: d.reason },
      audience: claim.audience,
      issuedAt: at,
      scope: this.id,
    });
    this.#advance(r);
    return r;
  }

  #advance(receipt: Claim): Claim {
    this.#lastReceipt = receipt;
    this.#receipts++;
    return receipt;
  }

  #reject(claim: Claim, at: number, d: Decision, viewer: ActorId): Decision {
    this.#remember({ claim, at, decision: d });
    return this.#redact(d, viewer);
  }

  #remember(r: Rejected): void {
    this.#rejected.push(deepFreeze(r));
    while (this.#rejected.length > this.#retain) {
      this.#rejected.shift();
      this.#evicted++;
    }
  }

  // Redaction: `matched` ids and `evidence` are cut to what the viewer may see. Each node keeps its ok/deny and
  // the number of ids removed (`hidden`), so a viewer can still learn that hidden claims exist and how many
  // matched, only not which. Hiding counts would hide the reason itself; that trade is the policy author's.
  // Recorded values (`value`, `n`) follow the same rule: a node loses them when it hid ids itself (v2.1: a `sum` over
  // hidden claims no longer keeps its total) or when any node beneath it (a lookup or a filter that fed the value) did,
  // so a hidden claim's fields never surface through a comparison.
  //
  // v2.2 (REDTEAM-3 L1): a quantifier that hid elements taints its DESCENDANTS too: they were evaluated with a hidden
  // `$item` bound, so their values (a cmp printing the hidden id) are cut. Their ok flags stay (a documented residual,
  // like the verdict itself). `thirdParty` (K1): time-dependent nodes keep no `hidden` count.
  #redact(d: Decision, viewer: ActorId, thirdParty = false): Decision {
    const seen = (id: string): boolean => {
      const e = this.#byId.get(id);
      return e !== undefined && visibleTo(e.claim, viewer);
    };
    const tainted = new Set<string>(); // proper ancestors of nodes that hid something
    const quantified: string[] = []; // body path prefixes of quantifiers that hid an element
    const trace = d.trace.map((t) => {
      if (!t.matched) return t;
      const kept = t.matched.filter(seen);
      const hidden = t.matched.length - kept.length;
      if (hidden === 0) return t;
      for (let i = t.path.indexOf('.'); i !== -1; i = t.path.indexOf('.', i + 1)) tainted.add(t.path.slice(0, i));
      quantified.push(`${t.path}.each[`, `${t.path}.some[`);
      const out = stripValues({ ...t, matched: kept, hidden });
      return thirdParty && isTimed(t) ? stripHidden(out) : out;
    });
    const under = (p: string): boolean => quantified.some((q) => p.startsWith(q));
    const cut =
      tainted.size === 0 && quantified.length === 0
        ? trace
        : trace.map((t) => ((t.value !== undefined || t.n !== undefined) && (tainted.has(t.path) || under(t.path)) ? stripValues(t) : t));
    return { ...d, trace: cut, evidence: d.evidence.filter(seen) };
  }

  #append(claim: Claim, at: number, receipt: Claim | undefined): number {
    const e: Entry = deepFreeze(receipt ? { claim, seq: this.#log.length, at, receipt } : { claim, seq: this.#log.length, at });
    this.#log.push(e);
    this.#byId.set(claim.id, e);
    push(this.#ix.byPredicate, claim.predicate, e);
    push(this.#ix.byAuthor, claim.author, e);
    if (claim.subject !== null) push(this.#ix.bySubject, claim.subject, e);
    for (const r of claim.refs) push(this.#ix.refsTo, r, e);
    return e.seq;
  }
}

function push(m: Map<string, Entry[]>, k: string, e: Entry): void {
  const l = m.get(k);
  if (!l) m.set(k, [e]);
  else if (l[l.length - 1] !== e) l.push(e); // a claim that repeats a ref indexes once
}

function evidenceOf(trace: TraceNode[]): string[] {
  return [...new Set(trace.flatMap((t) => t.matched ?? []))];
}

function stripHidden(t: TraceNode): TraceNode {
  const { hidden: _h, ...rest } = t;
  return rest;
}

function stripValues(t: TraceNode): TraceNode {
  const { value: _v, n: _n, ...rest } = t;
  return rest;
}
